"use client";

import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect, useRef, useState } from "react";
export default function CallToAction() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const imageRef = useRef<HTMLDivElement>(null);
  const [parallaxOffset, setParallaxOffset] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!imageRef.current) return;
      const rect = imageRef.current.getBoundingClientRect();
      const scrollProgress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
      setParallaxOffset(scrollProgress * 60 - 30);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="call-to-action" className="py-24 md:py-32 bg-background overflow-hidden">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {}
          <div ref={leftRef} className="space-y-8 transition-all duration-700 ease-out" style={{
          opacity: leftVisible ? 1 : 0,
          transform: leftVisible ? "translateX(0)" : "translateX(-60px)"
        }}>
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                Ready to Transform Your Financial Future?
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                Partner with modern accounting experts who leverage cloud technology and proactive strategies to help your business thrive in today's dynamic marketplace.
              </p>
            </div>

            <div className="space-y-6">
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="rounded-full bg-primary-light p-1 mt-1">
                    <div className="size-2 rounded-full bg-primary" />
                  </div>
                  <span className="text-base text-muted-foreground">Proactive tax planning and strategic financial advice</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="rounded-full bg-primary-light p-1 mt-1">
                    <div className="size-2 rounded-full bg-primary" />
                  </div>
                  <span className="text-base text-muted-foreground">Seamless cloud accounting integration with Xero, QuickBooks, and more</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="rounded-full bg-primary-light p-1 mt-1">
                    <div className="size-2 rounded-full bg-primary" />
                  </div>
                  <span className="text-base text-muted-foreground">Dedicated sector-specific expertise across diverse industries</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button asChild size="lg" className="group">
                
              </Button>
              
              <Button asChild variant="outline" size="lg" className="group border-border hover:border-accent hover:bg-accent-light transition-all duration-200">
                <Link to="/team-and-contact#contact-us" className="flex items-center gap-2">
                  Schedule Consultation
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={rightRef} className="relative transition-all duration-700 ease-out delay-150" style={{
          opacity: rightVisible ? 1 : 0,
          transform: rightVisible ? "translateX(0)" : "translateX(60px)"
        }}>
            <div className="relative rounded-lg overflow-hidden shadow-strong aspect-[4/5]">
              <div ref={imageRef} className="absolute inset-0 transition-transform duration-100 ease-out" style={{
              transform: `translateY(${parallaxOffset}px)`
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1542563/img/modern_finance_collaboration.png" alt="Financial planning and collaboration" className="w-full h-full object-cover scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/30 via-transparent to-transparent" />
              </div>
            </div>

            {}
            <div className="absolute -top-6 -right-6 size-24 rounded-full bg-accent/20 blur-2xl -z-10" />
            <div className="absolute -bottom-8 -left-8 size-32 rounded-full bg-primary/20 blur-3xl -z-10" />
          </div>
        </div>
      </div>
    </section>;
}